// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import type { AccessPolicy, Patient, ProjectMembership, ProjectMembershipAccess, Reference } from '@medplum/fhirtypes';
import type { SwePatientRow } from '../hooks/useSwePatients';

export const PATIENT_ACCESS_POLICY_NAME = 'software-engineer-patient-access-policy';

let cachedPatientPolicyRef: string | undefined;

/** Clears the session cache — intended for unit tests only. */
export function resetPatientAccessPolicyCache(): void {
  cachedPatientPolicyRef = undefined;
}

export async function resolvePatientAccessPolicyRef(medplum: MedplumClient): Promise<string> {
  if (cachedPatientPolicyRef) {
    return cachedPatientPolicyRef;
  }

  const policy = await medplum.searchOne('AccessPolicy', { name: PATIENT_ACCESS_POLICY_NAME });
  if (!policy?.id) {
    throw new Error(`AccessPolicy "${PATIENT_ACCESS_POLICY_NAME}" not found`);
  }

  cachedPatientPolicyRef = `AccessPolicy/${policy.id}`;
  return cachedPatientPolicyRef;
}

export function extractPatientIdsFromMembership(
  membership: ProjectMembership | undefined,
  patientPolicyRef: string
): string[] {
  return extractPatientIdsFromAccess(membership?.access ?? [], patientPolicyRef);
}

export function extractPatientIdsFromAccess(access: ProjectMembershipAccess[], patientPolicyRef: string): string[] {
  const patientIds: string[] = [];

  for (const entry of access) {
    if (entry.policy?.reference !== patientPolicyRef) {
      continue;
    }

    const patientParam = entry.parameter?.find((param) => param.name === 'patient');
    const patientRef = patientParam?.valueReference?.reference;
    if (!patientRef?.startsWith('Patient/')) {
      continue;
    }

    const patientId = patientRef.split('/')[1];
    if (patientId) {
      patientIds.push(patientId);
    }
  }

  return patientIds;
}

export function buildPatientAccessEntry(
  patientPolicyRef: Reference<AccessPolicy>,
  patientId: string
): ProjectMembershipAccess {
  return {
    policy: patientPolicyRef,
    parameter: [{ name: 'patient', valueReference: { reference: `Patient/${patientId}` } }],
  };
}

export function patientToSwePatientRow(patient: Patient): SwePatientRow {
  const id = patient.id ?? '';
  const name = [patient.name?.[0]?.given?.join(' '), patient.name?.[0]?.family].filter(Boolean).join(' ') || 'Unknown';
  const mrn =
    patient.identifier?.find((identifier) => identifier.system === 'http://polarissleep.com/mrn')?.value ?? '';
  const dob = patient.birthDate ?? '';
  return { id, name, mrn, dob };
}

export function sortSwePatientRows(rows: SwePatientRow[]): SwePatientRow[] {
  return [...rows].sort((a, b) => a.name.localeCompare(b.name));
}
